import React from "react";
import {
  Select,
  Box,
  Chip,
  MenuItem,
  Typography,
  Checkbox,
  ListItemIcon,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
// import { makeStyles } from "@mui/styles";
import _ from "underscore";
import { nanoid } from "nanoid";

import useLabels from "../../hooks/useLabels";
import { organizationLabelChipSx } from "./LabelChips";
import LabelEditDialog from "../../pages/home/children/admin/LabelEditDialog";

// const useStyles = makeStyles({
//   root: {
//     padding: 8,
//   },
//   selectMenu: {
//     minHeight: 35,
//   },
// });

const LabelSelect = ({
  id,
  useTimer = false,
  currentLabels,
  onLabelsChanged,
  width,
  size = "medium", // Add size prop for compact styling
  density = "default",
  disabled = false,
}) => {
  const [selectedLabels, setSelectedLabels] = React.useState(
    Array.isArray(currentLabels) ? currentLabels.filter(Boolean) : [],
  );

  const incomingSerialRef = React.useRef(
    JSON.stringify(Array.isArray(currentLabels) ? currentLabels : []),
  );

  React.useEffect(() => {
    const normalized = (Array.isArray(currentLabels) ? currentLabels : []).filter(Boolean);
    const nextSerial = JSON.stringify(normalized);
    if (incomingSerialRef.current !== nextSerial) {
      incomingSerialRef.current = nextSerial;
      setSelectedLabels(normalized.slice());
    }
  }, [currentLabels]);
  const [open, setOpen] = React.useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = React.useState(false);
  const timer = React.useRef();
  const paperRef = React.useRef(null);
  const closeCallbackRef = React.useRef(null);

  // Close the dropdown when the user scrolls the page,
  // but NOT when scrolling within the dropdown menu itself
  React.useEffect(() => {
    if (!open) return;
    const handleScroll = (event) => {
      if (paperRef.current && paperRef.current.contains(event.target)) {
        return; // scrolling inside the dropdown — ignore
      }
      handleClose();
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
    // handleClose is stable (empty deps) — no need in dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { labels, editLabelData } = useLabels();

  const ITEM_HEIGHT = size === "small" ? 36 : 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = React.useMemo(
    () => ({
      PaperProps: {
        ref: paperRef,
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          width: size === "small" ? 200 : 250,
          zIndex: 1300,
        },
      },
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      getContentAnchorEl: null,
    }),
    [ITEM_HEIGHT, ITEM_PADDING_TOP, size],
  );

  const setSelectedLabelsCallback = React.useCallback((event) => {
    const {
      target: { value },
    } = event;
    setSelectedLabels(Array.isArray(value) ? value.filter(Boolean) : []);
  }, []);

  const setSelectedLabelsClosedCallback = React.useCallback(() => {
    if (useTimer) {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        const changes = {
          protocolLabels: selectedLabels,
        };
        if (id) {
          localStorage.setItem(`LabelSelect_${id}`, JSON.stringify(changes));
        }
        if (onLabelsChanged) {
          onLabelsChanged(changes);
        }
      }, 500);
    } else {
      const changes = {
        protocolLabels: selectedLabels,
      };
      if (id) {
        localStorage.setItem(`LabelSelect_${id}`, JSON.stringify(changes));
      }
      if (onLabelsChanged) {
        onLabelsChanged(changes);
      }
    }
  }, [id, selectedLabels, onLabelsChanged, useTimer]);

  // Keep the ref pointing to the latest callback so the open→false effect
  // always calls the current version without needing it as a dep
  closeCallbackRef.current = setSelectedLabelsClosedCallback;

  // Stable close handler — closes the dropdown AND fires the save callback.
  // Called by: Done button click, MUI onClose (backdrop/Escape), page scroll.
  const handleClose = React.useCallback(() => {
    setOpen(false);
    if (closeCallbackRef.current) {
      closeCallbackRef.current();
    }
  }, []);

  function getLabel(id) {
    const value = _.findWhere(labels, { id: id });
    return value;
  }

  const isSelected = (id) =>
    Array.isArray(selectedLabels) && selectedLabels.indexOf(id) !== -1;

  return (
    <>
      <Select
        // classes={{ root: classes.root, selectMenu: classes.selectMenu }}
        sx={{
          width: width !== undefined && width !== null ? width : 100,
          minHeight: density === "compact" && size === "small" ? 36 : undefined,
        }}
        variant="outlined"
        size={size}
        multiple
        disabled={disabled}
        open={open}
        value={selectedLabels}
        onChange={setSelectedLabelsCallback}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        MenuProps={MenuProps}
        renderValue={(selected) => {
          if (!selected) {
            return null;
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => {
                if (!value) {
                  return null;
                }
                const label = getLabel(value);
                if (!label) {
                  return null;
                }
                return (
                  <Chip
                    variant="outlined"
                    size={density === "compact" ? "small" : size}
                    sx={organizationLabelChipSx(label, density)}
                    key={value}
                    label={label.name}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        {labels && labels.length > 0 ? (
          [
            ...labels.map((label) => (
              <MenuItem
                key={label.id}
                value={label.id}
                dense={size === "small"}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    size="small"
                    checked={isSelected(label.id)}
                    sx={{ p: 0.5 }}
                  />
                </ListItemIcon>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected(label.id) ? 600 : 400,
                  }}
                >
                  {label.name}
                </Typography>
              </MenuItem>
            )),
            <Divider key="done-divider" />,
            <MenuItem
              key="done-button"
              disableRipple
              onClick={handleClose}
              sx={{
                position: "sticky",
                bottom: 0,
                bgcolor: "background.paper",
                justifyContent: "center",
                py: 1,
                fontWeight: 600,
                color: "primary.main",
                zIndex: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Done
              {selectedLabels.filter(Boolean).length > 0 &&
                ` (${selectedLabels.filter(Boolean).length} selected)`}
            </MenuItem>,
            <MenuItem
              key="add-new-label"
              onClick={(e) => {
                e.stopPropagation();
                setLabelDialogOpen(true);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "primary.main",
                fontStyle: "italic",
                borderTop: 1,
                borderColor: "divider",
                mt: 1,
              }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">Add new label</Typography>
            </MenuItem>
          ]
        ) : (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setLabelDialogOpen(true);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "primary.main",
              fontStyle: "italic",
            }}
          >
            <AddIcon fontSize="small" />
            <Typography variant="body2">Create your first label</Typography>
          </MenuItem>
        )}
      </Select>

      <LabelEditDialog
        open={labelDialogOpen}
        label={null}
        onDialogClosed={async (data) => {
          setLabelDialogOpen(false);
          
          if (data) {
            // Create the new label
            const newLabel = {
              id: nanoid(),
              name: data.name,
              color: data.color,
              backgroundColor: data.backgroundColor,
              createdOn: new Date(),
              updatedOn: new Date(),
              index: labels ? labels.length : 0,
            };

            // Save the label to Firebase
            await editLabelData(newLabel);
            
            // Auto-select the newly created label
            const updatedSelection = [...selectedLabels, newLabel.id];
            setSelectedLabels(updatedSelection);
            
            // Notify parent component of the change
            const changes = {
              protocolLabels: updatedSelection,
            };
            if (id) {
              localStorage.setItem(`LabelSelect_${id}`, JSON.stringify(changes));
            }
            if (onLabelsChanged) {
              onLabelsChanged(changes);
            }
          }
        }}
      />
    </>
  );
};

export default LabelSelect;
